import { atom } from 'jotai';
import { CampaignData, GoogleAdsAccount, OrganizationDataWithUsersMembership, TaskTrackingResponse, HubSpotIntegrationStatus, Location, CalloutType, SiteLink, CampaignProximityInput, ExcludedLocation, MetaInterestEntry, MetaAudienceSelection } from './lib/types';
import { createUserDataAtom } from './lib/atom-registry';
import { isConversionDraftDirty, type CampaignConversionEdit } from './lib/conversion-draft';
import { rememberTabOrganization } from './lib/tab-organization';

export type FetchOrganization = () => Promise<void>;

// ---- Active-scope mirrors (#6450) ----------------------------------------
// The Jotai <Provider> in main.tsx creates its own store, so `getDefaultStore()`
// cannot read the app's atoms from non-React code. These module-level mirrors
// are written synchronously by the write paths of `organizationDataAtom` /
// `googleAdsAccountAtom` below (and cleared by `clearAllUserData`), so query-key
// builders in lib/query-options.ts always see the org/account the UI is on.
let activeOrganizationId: number | undefined;
let activeGoogleAdsAccountId: string | undefined;

// Helper to access the active organization id outside React (query keys, fetchers).
export function getCurrentOrganizationId(): number | undefined {
  return activeOrganizationId;
}

// Helper to access the selected Google Ads account id outside React (query keys).
export function getCurrentGoogleAdsAccountId(): string | undefined {
  return activeGoogleAdsAccountId;
}

// Called from clearAllUserData: resetting registered atoms bypasses the wrapper
// write paths below, so the mirrors (and the tab's remembered organisation, #6773)
// must be cleared explicitly with them.
export function clearActiveScopeMirrors(): void {
  activeOrganizationId = undefined;
  activeGoogleAdsAccountId = undefined;
  rememberTabOrganization(undefined);
}

const googleAdsAccountBaseAtom = createUserDataAtom<GoogleAdsAccount | null>(null);
export const googleAdsAccountAtom = atom(
  (get) => get(googleAdsAccountBaseAtom),
  (_get, set, update: GoogleAdsAccount | null) => {
    set(googleAdsAccountBaseAtom, update);
    activeGoogleAdsAccountId = update?.googleads_account_id ?? undefined;
  },
);
// True once AppShell's account-selection effect has run for the loaded org — before that,
// a null googleAdsAccountAtom means "not hydrated yet", not "no account". Google-data
// surfaces use this to show a loading state instead of flashing the no-account/empty
// placeholder on first paint (#5820).
export const googleAdsAccountResolvedAtom = createUserDataAtom<boolean>(false);

export const campaignDataAtom = createUserDataAtom<CampaignData | null>(null);
// Last-selected Meta campaign slug (#4660, #4659) — the Meta sibling of campaignDataAtom. Persisted
// per session/org so returning to /campaigns restores the Meta campaign you were on, exactly like
// Google. Mutually exclusive with campaignDataAtom: selecting a Meta campaign clears the Google
// selection and vice-versa, so the index can tell which platform was picked most recently.
export const metaCampaignDataAtom = createUserDataAtom<string | null>(null);

export const campaignsDataAtom = createUserDataAtom<CampaignData[]>([]);
export const campaignsMetaAtom = createUserDataAtom<{reason?: string; has_inaccessible_campaigns?: boolean} | null>(null);
export const isLoadingCampaignsAtom = createUserDataAtom<boolean>(false);
export const campaignsErrorAtom = createUserDataAtom<Error | null>(null);

export const isLoadingCampaignAtom = createUserDataAtom<boolean>(false);

export const campaignTaskTrackingAtom = createUserDataAtom<TaskTrackingResponse | undefined>(undefined);

const organizationDataBaseAtom = createUserDataAtom<OrganizationDataWithUsersMembership | null | undefined>(null);
export const organizationDataAtom = atom(
  (get) => get(organizationDataBaseAtom),
  (_get, set, update: OrganizationDataWithUsersMembership | null | undefined) => {
    set(organizationDataBaseAtom, update);
    activeOrganizationId = update?.id ?? undefined;
    // The tab's organisation, so a reload brings this tab back to it (#6773). Its only other writer is
    // openOrganizationInThisTab, for a link into an organisation that opens before any page has loaded.
    rememberTabOrganization(activeOrganizationId);
  },
);
export const fetchOrganizationDataAtom = createUserDataAtom<FetchOrganization | undefined>(undefined);

// Whether an organization switch is in flight (#6449). A shared atom, not
// per-hook useState: every `useOrganizationInitialization()` instance (top-bar
// switcher, layout overlay, org settings) must see the same flag so the
// "Switching organization" overlay stays up until the switch commits.
// Deliberately NOT registered for reset: clearAllUserData runs mid-switch and
// must not drop the overlay.
export const isSwitchingOrganizationAtom = atom<boolean>(false);

// Onboarding atom - in-memory storage for onboarding flow
interface OnboardingData {
  organizationName: string | null;
  organizationId: number | null;
  businessType: string | null;
  advertisingAudience: string | null;
  experienceLevel: string | null;
  currentAdvertisingSpend: string | null;
  completeData: Record<string, unknown> | null;
}

export const onboardingDataAtom = createUserDataAtom<OnboardingData>({
  organizationName: null,
  organizationId: null,
  businessType: null,
  advertisingAudience: null,
  experienceLevel: null,
  currentAdvertisingSpend: null,
  completeData: null
});


// Campaign tab selection atom - persists selected tab across campaigns
export const selectedCampaignTabAtom = atom<string>('keywords');

// Meta campaign detail tab (#5981) — the Meta sibling of selectedCampaignTabAtom, so leaving a
// Meta campaign and coming back restores the tab you were on (Google parity).
export const selectedMetaCampaignTabAtom = atom<string>('overview');

// Meta ad-set surface tab (#6210) — the Meta sibling of selectedAdTabAtom (Google's ad-group page).
export const selectedMetaAdSetTabAtom = atom<string>('overview');

/**
 * Staged Budget-tab edits for a Meta campaign (#5981) — the sections stage values here and the
 * detail toolbar's Save Changes commits them, mirroring Google's draft + toolbar-save flow.
 * Keyed by campaignId so a draft never leaks across campaigns; undefined fields = untouched.
 */
export interface MetaBudgetDraft {
  campaignId: number;
  budgetWhole?: number;
  bidStrategy?: 'LOWEST_COST_WITHOUT_CAP' | 'COST_CAP';
  capText?: string;
  cplText?: string;
}
export const metaBudgetDraftAtom = createUserDataAtom<MetaBudgetDraft | null>(null);

/**
 * The Targeting → Interests staged edits (#6076 followup). Adds/removes stage here (campaign-keyed);
 * the campaign toolbar's Save Changes commits them in one Meta write, in place of Go Live.
 */
export interface MetaInterestsDraft {
  campaignId: number;
  interests: MetaInterestEntry[];
}
export const metaInterestsDraftAtom = createUserDataAtom<MetaInterestsDraft | null>(null);

/**
 * Targeting → Placements staged edits (#6076 followup). The interactive tree stages its checked
 * state here (campaign-keyed); the toolbar's Save Changes converts it to a placement selection and
 * commits in one Meta write. Checked state (not the selection) is stored so an in-progress "0
 * platforms" is distinct from "all platforms = Advantage+ automatic".
 */
export interface MetaPlacementsDraft {
  campaignId: number;
  checkedPlatforms: string[];
  checkedPositions: Record<string, string[]>;
}
export const metaPlacementsDraftAtom = createUserDataAtom<MetaPlacementsDraft | null>(null);

/**
 * Targeting → Audience staged edits (#6076 followup). The Edit Audience modal's Done button stages
 * the whole demographic slice here (campaign-keyed); the toolbar's Save Changes commits it in one
 * Meta write, in place of Go Live. Replace-whole-object, matching the targeting PATCH semantics.
 */
export interface MetaAudienceDraft {
  campaignId: number;
  audience: MetaAudienceSelection;
}
export const metaAudienceDraftAtom = createUserDataAtom<MetaAudienceDraft | null>(null);

/**
 * Targeting → Locations staged edits (#6178 followup). The Locations sub-tab's picker edits this
 * campaign-keyed draft live (like Google's CampaignLocationsSection edits the campaign draft); its
 * Done button just closes, and the toolbar's Save Changes commits the geo + radius selection in one
 * Meta write, in place of Go Live. Geo is held as resolved ``Location`` rows (mirroring Google's
 * draft) so the table/picker render without re-resolving criteria ids.
 */
export interface MetaLocationsDraft {
  campaignId: number;
  locations: Location[];
  radii: CampaignProximityInput[];
}
export const metaLocationsDraftAtom = createUserDataAtom<MetaLocationsDraft | null>(null);

// campaignId → epoch ms of the last on-open external-change check (#5566 Req 1). In-memory only
// (no persistence): a full reload intentionally re-checks. Throttles auto-pull-on-open to once
// per 15 min per campaign across in-session navigation.
export const syncedCampaignCheckTimestampsAtom = createUserDataAtom<Record<number, number>>({});

// Ad tab selection atom - persists selected tab across ad navigation
export const selectedAdTabAtom = atom<string>('overview');

// Top-level page tab atoms — persist the last-visited tab so clicking a sidebar nav item
// restores where you left off rather than always landing on the default tab.
// Pattern: read with useAtom, fall back to savedTab when hash is absent, write on every tab change.
// See frontend/CLAUDE.md § "Tab persistence pattern" for the canonical implementation.
export const selectedAssetsTabAtom = atom<string>('landing-pages');
export const selectedAssetsGoogleSubTabAtom = atom<string>('callouts');
export const mediaViewModeAtom = atom<'grid' | 'table'>('grid');
// A landing page's URL carried from the Assets table into the Products view
// (#6484), which shows the rows for that URL until cleared. Sitelinks used to
// do the same; it now arrives as a selection in the Google tab's own filter
// (#6745) rather than a banner the reader cannot widen.
export const productsUrlFilterAtom = atom<string | null>(null);
// Documents library (#6232): tile browser vs master-detail rows.
export const documentsViewModeAtom = atom<'grid' | 'list'>('grid');
export const selectedBusinessTabAtom = atom<string>('overview');
// Legacy Performance page (Traffic/Cost/Looker), live again at /performance while the
// consolidated page sits at /performance-beta.
export const selectedPerformanceTabAtom = atom<string>('traffic');
// Setup page tabs (#6011): Onboarding checklist + the Strategy (Goals) tab moved out of the sidebar.
export const selectedSetupTabAtom = atom<string>('onboarding');
// Consolidated Performance surface (#5971): master platform scope + active tab.
export const consolidatedPerformanceScopeAtom = atom<'all' | 'google' | 'meta'>('all');
export const selectedConsolidatedPerformanceTabAtom = atom<string>('overview');
// Consolidated Performance filter toolbar state (#5971) — shared UI state the
// below-tabs toolbar writes and the Overview reads (other tabs to follow, #5994).
// The date range applies to every platform in scope; comparison + campaign
// filters are Google concepts.
export const consolidatedPerformanceDateRangeAtom = atom<number>(14);
// #6500 — an explicit custom window from the date picker. Non-null overrides
// the day-count preset above; choosing any preset clears it.
export const consolidatedPerformanceCustomRangeAtom = atom<{ start: string; end: string } | null>(null);
export const consolidatedPerformanceGroupByAtom = atom<'day' | 'week' | 'month'>('day');
export const consolidatedPerformanceComparisonAtom = atom<string>('none');
export const consolidatedPerformanceCampaignTypesAtom = atom<Set<string>>(new Set<string>(['all']));
export const consolidatedPerformanceCampaignIdsAtom = createUserDataAtom<Set<number>>(new Set<number>());
export const consolidatedPerformanceIncludeExternalAtom = atom<boolean>(false);
// Keywords & Terms sub-view: an atom (not panel-local state) so Overview's
// "View all" links can land on the right sub-view (09-08).
export const consolidatedKeywordsSubViewAtom = atom<'keywords' | 'search-terms'>('keywords');
// #6279 — which org the campaign-scoped filters above belong to. The page
// remounts on org change (withOrgReset) but atoms are global, so a campaign
// selection from org A would silently empty org B's page; the shell compares
// this marker on mount and resets the campaign-scoped filters when it differs.
export const consolidatedPerformanceFilterOrgAtom = createUserDataAtom<number | null>(null);
export const selectedOptimizationsTabAtom = atom<string>('call-actions');
export const selectedOrganizationsTabAtom = atom<string>('overview');
export const selectedSettingsAccountTabAtom = atom<string>('account');

// Selected ad ID atom - tracks which ad is currently selected in the ads view
export const selectedAdIdAtom = createUserDataAtom<number | null>(null);
// Tracks which ad is currently being rebuilt (shown as spinner in sidebar)
export const rebuildingAdIdAtom = createUserDataAtom<number | null>(null);
// Tracks a newly created ad ID so the generating banner shows immediately before task tracking catches up
export const newlyCreatedAdIdAtom = createUserDataAtom<number | null>(null);
export const campaignSidebarPageAtom = createUserDataAtom<number>(1);

// Ad preview modal state - controls whether the ad preview modal is shown
export const showAdPreviewModalAtom = atom<boolean>(false);

// Campaign creation flow - stores pre-selections before navigating to /campaigns/new
interface CampaignCreationPreSelection {
  // String[] rather than a tight enum: the type-first picker (#4277 chunk 4b) writes its row ids
  // (``'prospecting' | 'hipo' | 'retargeting' | 'brand' | …``) here, while legacy readers
  // (``new/_layout/campaign/prospecting.tsx``) filter back down to ``CAMPAIGN_TYPE`` via
  // ``Object.values(CAMPAIGN_TYPE).includes(...)``. Pre-4277 callers wrote ``CAMPAIGN_TYPE`` /
  // ``'meta'`` values; both shapes are valid here.
  types: string[];
  competitorIds: string[];
  origin?: string; // Route to navigate back to when canceling/going back from first step
}

export const campaignCreationPreSelectionAtom = createUserDataAtom<CampaignCreationPreSelection>({
  types: [],
  competitorIds: [],
});

// #5975: the wizard picker's platform tab (Google / Meta) must survive Back-navigation from a
// wizard step. The row id alone can't restore it — Prospecting/HiPo exist on both platforms, so
// deriving the tab from the row snaps shared rows back to Google. Tab-persistence pattern: URL
// hash (OAuth round trips) takes precedence, this atom is the in-session fallback.
export const selectedNewCampaignPlatformAtom = atom<'google' | 'meta' | null>(null);

// #4554: when /add-to-campaign is launched from a specific competitor campaign
// page, skip the SELECT_CAMPAIGN step and pre-select that campaign. originSlug
// is used to route Back/Cancel to the originating campaign instead of /competitors.
interface AddToCampaignPreSelection {
  campaignId: number;
  originSlug: string;
}

export const addToCampaignPreSelectionAtom = createUserDataAtom<AddToCampaignPreSelection | null>(null);

// #6715: Add to campaign moved from the Competitors toolbar onto the row, so
// the flow opens with the competitor the reader was looking at already ticked
// instead of every competitor. Cleared by /add-to-campaign once it has read it.
export const addToCampaignCompetitorIdAtom = createUserDataAtom<string | null>(null);

// HubSpot integration atom
export const hubspotIntegrationAtom = createUserDataAtom<HubSpotIntegrationStatus | null>(null);

// Campaign draft atom - stores unsaved changes for the campaign settings
// This allows a single "Save Changes" button to save all modifications across tabs
export interface CampaignDraft {
  campaignId: number | null;
  name: string;
  businessDescription: string;
  locations: Location[];
  budget: number;
  targetCpa: number;
  targetRoas: number | undefined;
  biddingStrategyType: string;
  callouts: CalloutType[];
  siteLinks: SiteLink[];
  // Radius (proximity) targets in write shape. undefined = untouched (radii left
  // unchanged on save); [] = clear all; non-empty = full-replace (customizes).
  proximityTargets?: CampaignProximityInput[] | null;
  // Excluded (negative) locations, staged like the rest of the draft. undefined = not yet
  // hydrated from the excluded-locations query (left untouched on save).
  excludedLocations?: ExcludedLocation[];
  // Conversions-tab row edits (#6479), staged like excluded locations. undefined = not yet
  // hydrated from the campaign conversion-actions query (left untouched on save).
  conversionActions?: CampaignConversionEdit[];
}

export interface CampaignDraftState {
  draft: CampaignDraft | null;
  original: CampaignDraft | null;
}

export const campaignDraftStateAtom = createUserDataAtom<CampaignDraftState>({
  draft: null,
  original: null,
});

// Derived atom to check if there are unsaved changes
export const campaignIsDirtyAtom = atom((get) => {
  const state = get(campaignDraftStateAtom);
  if (!state.draft || !state.original) return false;

  return (
    state.draft.name !== state.original.name ||
    state.draft.businessDescription !== state.original.businessDescription ||
    state.draft.budget !== state.original.budget ||
    state.draft.targetCpa !== state.original.targetCpa ||
    state.draft.targetRoas !== state.original.targetRoas ||
    state.draft.biddingStrategyType !== state.original.biddingStrategyType ||
    JSON.stringify(state.draft.locations) !== JSON.stringify(state.original.locations) ||
    JSON.stringify(state.draft.callouts) !== JSON.stringify(state.original.callouts) ||
    JSON.stringify(state.draft.siteLinks) !== JSON.stringify(state.original.siteLinks) ||
    JSON.stringify(state.draft.proximityTargets) !== JSON.stringify(state.original.proximityTargets) ||
    JSON.stringify(state.draft.excludedLocations) !== JSON.stringify(state.original.excludedLocations) ||
    // Field-by-field so every editable conversion field arms Save Changes (#6472 lesson).
    isConversionDraftDirty(state.draft.conversionActions, state.original.conversionActions)
  );
});

// Campaign ID currently transitioning state (pause/resume) — used by sidebar to show spinner
export const transitioningCampaignIdAtom = createUserDataAtom<number | null>(null);

// Which UI surface ('go_live_button', 'deploy_button', 'approval_flow', 'pause_toggle')
// dispatched the in-flight state transition, and its TARGET state, keyed by campaign id.
// Every button polls the same task tracking, so without attribution a launch lights up
// several spinners at once (#5663). The target is shared because multiple hook instances
// exist per campaign (page toolbar + actions dropdown) — an instance that merely adopts
// the in-flight task would otherwise guess the target from the current state and label a
// pause as "Resuming..." (#5669). Cleared when the transition completes; empty after a
// reload, which callers treat as "resumed, owner unknown".
export type CampaignTransitionInfo = { source: string | undefined; target: string | null };
export const campaignTransitionInitiatorAtom = createUserDataAtom<Record<number, CampaignTransitionInfo | undefined>>({});

// Test mode atom - local override for PostHog feature flag
// This allows instant reactivity when toggled in Admin menu.
// #6266: defaults ON for staging builds only, so reviewers signed up as non-admin users see
// test-mode surfaces without the Admin toggle. Production builds keep false. (Meta itself is
// no longer behind it, #6678.)
export const testModeAtom = atom<boolean>(import.meta.env.VITE_NODE_ENV === 'staging');

// In-memory preference for the radius input unit (km vs mi). Resets on full
// reload — localStorage is forbidden per CLAUDE.md. Cross-session
// persistence would need a user-settings backend field.
export type RadiusUnit = 'km' | 'mi';
export const radiusUnitAtom = atom<RadiusUnit>('km');
export const KM_PER_MILE = 1.609344;
/** Display a stored km radius in the given unit, as a whole number. */
export const radiusInUnit = (km: number, unit: RadiusUnit): number =>
  unit === 'mi' ? Math.round(km / KM_PER_MILE) : Math.round(km);

// Manual brand entry mode — set when user clicks "Enter Details Manually" from the
// brand intel failure state on the business page. Tells the brand wizard to let the
// user go through all steps and save data without re-triggering brand intel.
export const manualBrandEntryAtom = atom<boolean>(false);

// Performance V2 widget layout — keyed by tab ID, each tab has its own widget order/sizing
export interface WidgetLayout {
  id: string
  width: 33 | 50 | 66 | 100
  order: number
}

export const performanceWidgetLayoutAtom = createUserDataAtom<Record<string, WidgetLayout[]>>({});
// Consolidated Performance surface (#5971): per-tab widget layout (drag/resize), org-scoped.
export const consolidatedPerformanceWidgetLayoutAtom = createUserDataAtom<Record<string, WidgetLayout[]>>({});

// ---- Strategy Goals page state ------------------------------------------
// The Goals page tracks which channels the org runs and which channel goals
// are checked off. Persisted per-org via /api/strategy/goals-state; these
// atoms are the in-memory source of truth the page hydrates into and writes
// through on every toggle.

export type StrategyFocusKpi = 'budget' | 'cpa' | 'conversions' | 'roas';

export interface StrategyKpiTargets {
  cpa: number;
  roas: number;
  budget: number;
  conversions: number;
  aov: number;
  focusKpi: StrategyFocusKpi;
}

export const strategyKpiTargetsAtom = createUserDataAtom<StrategyKpiTargets>({
  cpa: 0,
  roas: 0,
  budget: 0,
  conversions: 0,
  aov: 0,
  focusKpi: 'cpa',
});

export type StrategyChannelId =
  | 'glitch'
  | 'search'
  | 'display'
  | 'meta'
  | 'tracking'
  | 'website'
  | 'awards';

// Exported so the Goals page can REPLACE the atom from the server blob on
// hydration (defaults + saved blob) instead of merging over another org's
// values (#6452).
export const STRATEGY_ENABLED_CHANNELS_DEFAULT: Record<StrategyChannelId, boolean> = {
  glitch: true,
  search: true,
  display: false,
  meta: false,
  tracking: true,
  website: true,
  awards: true,
};

export const strategyEnabledChannelsAtom = createUserDataAtom<Record<StrategyChannelId, boolean>>(
  STRATEGY_ENABLED_CHANNELS_DEFAULT,
);

export const strategyCompletedGoalsAtom = createUserDataAtom<Record<string, boolean>>({});

// AI landing pages (#5701): the generated page's public_id per campaign id. The backend has no
// by-campaign lookup, so the campaign tab tracks it here (survives in-session nav; resets on reload).
export const landingPageByCampaignAtom = createUserDataAtom<Record<number, string>>({});

// Landing pages by business (#6480 decouple): the generated pages' public_ids per business id. A
// userData atom, so it resets on org switch / logout alongside the other per-org caches.
export const landingPagesByBusinessAtom = createUserDataAtom<Record<number, string[]>>({});

